import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';
import { useSEO } from '@/hooks/useSEO';
import { categories } from '@/mocks/categories';
import { getFeaturedComics, getHotComics, getNewComics, getLatestUpdates } from '@/mocks/comics';

export default function Home() {
  const { t } = useTranslation();

  const featured = getFeaturedComics();
  const hot = getHotComics();
  const newComics = getNewComics();
  const latest = getLatestUpdates();

  useSEO({
    title: '韩国免费漫画_下拉式韩漫大全_韩漫在线阅读-漫画之家',
    description: '在漫画之家，您能免费阅读下拉式韩漫大全，在线浏览各种韩国漫画，体验流畅的阅读服务，轻松探索人气作品并随时追更喜爱故事内容。',
    keywords: '韩国免费漫画,下拉式韩漫大全,韩漫在线阅读,漫画之家',
    canonicalUrl: 'https://www.manhuazhijia.com',
    ogImage: 'https://readdy.ai/api/search-image?query=A%20vibrant%20Korean%20manhwa%20webtoon%20inspired%20collage%20with%20colorful%20comic%20panels%20layered%20over%20a%20warm%20gradient%20background%20soft%20pink%20orange%20and%20cream%20tones%20various%20manhwa%20style%20characters%20in%20dynamic%20poses%20clean%20modern%20composition&width=1200&height=630&seq=home-og-image&orientation=landscape',
    schemas: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: '漫画之家',
        url: 'https://www.manhuazhijia.com',
        description: t('site.description'),
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://www.manhuazhijia.com/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '漫画之家',
        url: 'https://www.manhuazhijia.com',
        description: t('site.description'),
        address: {
          '@type': 'PostalAddress',
          addressLocality: '上海',
          addressCountry: 'CN',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'contact@manhuazhijia.com',
          contactType: 'customer service',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: '漫画之家 - 韩国免费漫画在线阅读',
        description: t('site.description'),
        url: 'https://www.manhuazhijia.com',
        isPartOf: {
          '@type': 'WebSite',
          name: '漫画之家',
        },
      },
    ],
  });

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />

      <main>
        <section className="relative h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20and%20artistic%20digital%20illustration%20of%20a%20cozy%20reading%20nook%20surrounded%20by%20floating%20comic%20book%20pages%20and%20colorful%20manhwa%20panels%20in%20the%20air%20vibrant%20but%20soft%20color%20palette%20with%20pink%20coral%20peach%20cream%20and%20warm%20beige%20tones%20magical%20dreamy%20atmosphere%20with%20soft%20glowing%20particles%20abstract%20artistic%20style%20modern%20webtoon%20inspired%20composition%20harmonious%20and%20inviting%20mood&width=1600&height=900&seq=home-hero-bg&orientation=landscape"
            alt="韩国免费漫画 - 漫画之家"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {t('home.heroTitle')}
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
              {t('home.heroSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/rexiao"
                className="bg-primary-500 hover:bg-primary-600 text-white px-8 py-3 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap"
              >
                {t('home.heroCta')}
              </Link>
              <Link
                to="/about"
                className="bg-white/10 hover:bg-white/20 text-white border border-white/30 px-8 py-3 rounded-full font-medium text-sm transition-all duration-300 backdrop-blur-sm whitespace-nowrap"
              >
                {t('nav.about')}
              </Link>
            </div>
          </div>
        </section>

        {featured.length > 0 && (
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 -mt-16 relative z-10 mb-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {featured.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.id}`}
                  className="group flex bg-white rounded-lg overflow-hidden hover:shadow-md transition-all duration-300"
                >
                  <div className="w-24 h-32 flex-shrink-0 overflow-hidden">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 p-4 flex flex-col justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1 line-clamp-2 leading-relaxed">
                        {comic.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-foreground-400 mt-2">
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-amber-400 text-xs"></i>
                        {comic.rating}
                      </span>
                      <span>{comic.author}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900">
              {t('home.categoryTitle')}
            </h2>
            <p className="text-sm text-foreground-500 mt-2">{t('home.categoryDesc')}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-lg hover:-translate-y-1 hover:shadow-md transition-all duration-300 group"
              >
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center group-hover:bg-primary-500 transition-colors duration-300">
                  <i className={`${cat.icon} text-lg text-primary-500 group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <span className="text-xs font-medium text-foreground-700 whitespace-nowrap">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900">
                {t('home.hotTitle')}
              </h2>
              <p className="text-sm text-foreground-500 mt-1">{t('home.hotDesc')}</p>
            </div>
            <Link
              to="/category/rexiao"
              className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap"
            >
              {t('home.viewAll')} <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {hot.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </section>

        {newComics.length > 0 && (
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
            <div className="mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900">
                新作上架
              </h2>
              <p className="text-sm text-foreground-500 mt-1">最新上线的精彩韩漫</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {newComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </section>
        )}

        <section className="bg-white py-10">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900">
                  {t('home.latestTitle')}
                </h2>
                <p className="text-sm text-foreground-500 mt-1">{t('home.latestDesc')}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {latest.slice(0, 8).map((comic) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.id}`}
                  className="flex items-center gap-4 p-4 rounded-lg hover:bg-background-100 transition-colors duration-200 group"
                >
                  <div className="w-14 h-18 flex-shrink-0 rounded-md overflow-hidden">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">
                      {comic.author}
                    </p>
                    <p className="text-xs text-foreground-400 mt-0.5">
                      更新于 {comic.updateDate}
                    </p>
                  </div>
                  <span className="text-xs text-accent-500 font-medium bg-accent-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                    {comic.status}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <div
            className="relative rounded-2xl overflow-hidden p-10 md:p-16 text-center"
            style={{
              backgroundImage: 'url(https://readdy.ai/api/search-image?query=A%20warm%20atmospheric%20background%20with%20soft%20abstract%20watercolor%20textures%20in%20coral%20peach%20and%20cream%20tones%20gentle%20gradient%20with%20subtle%20floating%20comic%20panel%20silhouettes%20cozy%20and%20inviting%20mood%20artistic%20minimal%20composition%20no%20text%20no%20people&width=1400&height=600&seq=home-cta-bg&orientation=landscape)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div className="absolute inset-0 bg-black/30"></div>
            <div className="relative z-10">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                {t('home.ctaTitle')}
              </h2>
              <p className="text-white/80 text-sm md:text-base mb-6 max-w-lg mx-auto">
                {t('home.ctaDesc')}
              </p>
              <Link
                to="/category/rexiao"
                className="inline-flex bg-primary-500 hover:bg-primary-600 text-white px-8 py-3 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap"
              >
                {t('home.ctaButton')}
              </Link>
            </div>
          </div>
        </section>

        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
          <div className="text-center mb-8">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900">
              常见问题
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                q: '漫画之家的韩漫是免费的吗？',
                a: '是的！漫画之家上的所有韩国漫画均免费提供在线阅读，您无需支付任何费用即可畅享海量韩漫资源。',
              },
              {
                q: '什么是在下拉式韩漫？',
                a: '下拉式韩漫是一种优化的阅读方式，您只需向下滚动页面即可连续阅读漫画内容，无需一页页点击翻页，带来更流畅的阅读体验。',
              },
              {
                q: '漫画更新频率是怎样的？',
                a: '我们与多家韩国漫画平台合作，通常在原作更新后24小时内同步更新中文版本，让您第一时间追更喜爱的作品。',
              },
              {
                q: '需要注册账号才能阅读吗？',
                a: '不需要！漫画之家支持无需注册即可免费阅读所有漫画内容，我们希望为读者提供最便捷的阅读体验。',
              },
            ].map((faq, index) => (
              <details key={index} className="group bg-white rounded-lg p-5 cursor-pointer">
                <summary className="flex items-center justify-between text-sm font-semibold text-foreground-900 list-none">
                  {faq.q}
                  <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                </summary>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: [
                  { '@type': 'Question', name: '漫画之家的韩漫是免费的吗？', acceptedAnswer: { '@type': 'Answer', text: '是的！漫画之家上的所有韩国漫画均免费提供在线阅读，您无需支付任何费用即可畅享海量韩漫资源。' } },
                  { '@type': 'Question', name: '什么是在下拉式韩漫？', acceptedAnswer: { '@type': 'Answer', text: '下拉式韩漫是一种优化的阅读方式，您只需向下滚动页面即可连续阅读漫画内容，无需一页页点击翻页，带来更流畅的阅读体验。' } },
                  { '@type': 'Question', name: '漫画更新频率是怎样的？', acceptedAnswer: { '@type': 'Answer', text: '我们与多家韩国漫画平台合作，通常在原作更新后24小时内同步更新中文版本，让您第一时间追更喜爱的作品。' } },
                  { '@type': 'Question', name: '需要注册账号才能阅读吗？', acceptedAnswer: { '@type': 'Answer', text: '不需要！漫画之家支持无需注册即可免费阅读所有漫画内容，我们希望为读者提供最便捷的阅读体验。' } },
                ],
              }),
            }}
          />
        </section>
      </main>

      <Footer />
    </div>
  );
}